import 'src/global.css';

import { useEffect } from 'react';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';
import { WebSocketProvider } from 'src/contexts/websocket-context';
import { UserPresenceSocketBridge } from 'src/contexts/user-presence-socket-bridge';

import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { AuthProvider } from 'src/auth/context/jwt';

import { WS_URL } from './config';
import { Snackbar } from './components/snackbar';
import { LocalizationProvider } from './locales';
import { ChannelProvider } from './pages/channel/hooks';
import { PremissionProvider } from './pages/auth/hooks';
import { CampaignProvider } from './pages/campaign/hooks/campaign-provider';
import { CallPopup } from './pages/chat/channel/call/components/call-popup';
import { CallDialog } from './pages/chat/channel/call/components/call-dialog';
import { WhatsAppCallProvider } from './pages/chat/channel/whatsapp/call/hooks';
import { WhatsAppCallPopup } from './pages/chat/channel/whatsapp/call/components/whatsapp-call-popup';

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();
  checkAppVersion();

  return (
    <AuthProvider>
      <PremissionProvider>
        <SettingsProvider defaultSettings={defaultSettings}>
          <LocalizationProvider>
            <ThemeProvider
              noSsr
              defaultMode={themeConfig.defaultMode}
              modeStorageKey={themeConfig.classesPrefix}
            >
              <MotionLazy>
                <CampaignProvider>
                  <ChannelProvider>
                    <Snackbar />
                    <ProgressBar />
                    <CallDialog />
                    <CallPopup />
                    <SettingsDrawer defaultSettings={defaultSettings} />
                    <WebSocketProvider url={WS_URL}>
                      <WhatsAppCallProvider>
                        <WhatsAppCallPopup />
                        <UserPresenceSocketBridge />
                        {children}
                      </WhatsAppCallProvider>
                    </WebSocketProvider>
                  </ChannelProvider>
                </CampaignProvider>
              </MotionLazy>
            </ThemeProvider>
          </LocalizationProvider>
        </SettingsProvider>
      </PremissionProvider>
    </AuthProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// versionCheck.js
const clearServiceWorkerAndCache = async () => {
  // Unregister all service workers
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      await reg.unregister();
    }
  }

  // Clear all browser caches
  if ('caches' in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));
  }
};

const checkAppVersion = async () => {
  try {
    const response = await fetch(`/version.json?v=${Date.now()}`, { cache: 'no-store' });
    const data = await response.json();

    const currentVersion = data.version;
    const savedVersion = localStorage.getItem('app_version');

    const shouldReload = !savedVersion || savedVersion !== currentVersion;

    if (shouldReload) {
      console.log(`App version changed from ${savedVersion} to ${currentVersion}. Reloading...`);
      localStorage.setItem('app_version', currentVersion);

      await clearServiceWorkerAndCache();

      // Force reload without cache
      window.location.replace(
        `${window.location.origin}${window.location.pathname}?reload=${Date.now()}`
      );
    }
  } catch (error) {
    console.error('Version check failed:', error);
  }
};
